/**
 * Likes Service
 * Handles like/unlike operations for content modules
 */

import { api } from './api.js';

// Local cache for like states
const likeCache = new Map();

/**
 * Toggle like status for a module
 * Uses optimistic UI updates with rollback on error
 *
 * @param {string} moduleId - The module ID
 * @param {string} profileId - The profile ID
 * @param {string} deviceId - The device ID
 * @returns {Promise<{liked: boolean, likesCount: number}>}
 */
export async function toggleLike(moduleId, profileId, deviceId) {
    // Get current state from cache
    const cacheKey = `${moduleId}_${profileId}`;
    const previousState = likeCache.get(cacheKey) || false;

    // Optimistic update: toggle immediately in cache
    const optimisticState = !previousState;
    likeCache.set(cacheKey, optimisticState);

    try {
        // Make API call
        const response = await api.post(
            `/api/device/modules/${moduleId}/like`,
            null,
            {
                deviceId,
                profileId
            }
        );

        // Update cache with server response
        likeCache.set(cacheKey, response.liked);

        return {
            liked: response.liked,
            likesCount: response.likesCount
        };
    } catch (error) {
        // Rollback on error: revert to previous state
        console.error('Failed to toggle like:', error);
        likeCache.set(cacheKey, previousState);

        // Re-throw error so UI can handle it
        throw error;
    }
}

/**
 * Get all liked modules for a profile
 *
 * @param {string} profileId - The profile ID
 * @param {string} deviceId - The device ID
 * @returns {Promise<Object>} Map of moduleId -> boolean
 */
export async function getProfileLikes(profileId, deviceId) {
    try {
        const response = await api.get(
            `/api/device/profiles/${profileId}/likes`,
            {
                deviceId
            }
        );

        // Update cache with all likes
        const likes = response.likes || {};
        Object.entries(likes).forEach(([moduleId, isLiked]) => {
            const cacheKey = `${moduleId}_${profileId}`;
            likeCache.set(cacheKey, isLiked);
        });

        return likes;
    } catch (error) {
        console.error('Failed to get profile likes:', error);
        // Return empty object on error
        return {};
    }
}

/**
 * Get like state for a specific module from cache
 *
 * @param {string} moduleId - The module ID
 * @param {string} profileId - The profile ID
 * @returns {boolean} True if liked, false otherwise
 */
export function getLikeState(moduleId, profileId) {
    const cacheKey = `${moduleId}_${profileId}`;
    return likeCache.get(cacheKey) || false;
}

/**
 * Clear the like cache
 * Useful when switching profiles
 */
export function clearLikeCache() {
    likeCache.clear();
}
